import type { z } from "zod";

import instance from "..";
import { looseParse } from "../looseParse";
import {
  getSecfiLoansResponseSchema,
  type secfiLoanAnalyticsInterestSchema,
  secfiLoanAnalyticsSchema,
  type secfiLoanInterestSchema,
  type secfiLoanSchema,
} from "./loans.schema";

// API Response Types (snake_case - matches API contract)
export type SecfiLoanInterest = z.infer<typeof secfiLoanInterestSchema>;

export type SecfiLoan = z.infer<typeof secfiLoanSchema>;

export type GetSecfiLoansResponse = z.infer<typeof getSecfiLoansResponseSchema>;

export interface GetSecfiLoansParams {
  accountId: string;
  start?: string;
  end?: string;
  limit?: number;
  pageToken?: string;
}

export const getSecfiLoans = async ({
  accountId,
  start,
  end,
  limit = 1000,
  pageToken,
}: GetSecfiLoansParams): Promise<GetSecfiLoansResponse> => {
  // Calculate tomorrow's date for default end date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0]; // YYYY-MM-DD format

  const params: Record<string, string | number> = {
    account_id: accountId,
    start: start || "1970-01-01",
    end: end || tomorrowStr,
    limit,
  };

  if (pageToken) {
    params.page_token = pageToken;
  }

  const res = await instance.get<GetSecfiLoansResponse>("/fpsl/loans", {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    params,
  });

  return looseParse(getSecfiLoansResponseSchema, res.data, "getSecfiLoans");
};

export const getAllSecfiLoans = async ({
  accountId,
  start,
  end,
  pageLimit = 1000,
  sortOrder = "desc",
}: GetAllSecfiLoansParams): Promise<SecfiLoan[]> => {
  let allLoans: SecfiLoan[] = [];
  let nextPageToken: string | null = null;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await getSecfiLoans({
      accountId,
      start,
      end,
      limit: pageLimit,
      pageToken: nextPageToken || undefined,
    });

    // `response.loans` / `next_page_token` are normally guaranteed by the
    // schema, but `looseParse` falls back to the raw (possibly shapeless)
    // payload on a whole-envelope validation failure. Guard the spread so a
    // malformed page can't throw ("loans is not iterable"), and only continue
    // paginating on a real string cursor so a non-string `next_page_token`
    // can't spin an infinite loop.
    const loans = Array.isArray(response.loans) ? response.loans : [];
    allLoans = [...allLoans, ...loans];
    nextPageToken = response.next_page_token;
    hasMorePages = typeof nextPageToken === "string" && nextPageToken !== "";
  }

  // Sort by date - API returns asc by date, so reverse for desc
  if (sortOrder === "desc") {
    return allLoans.reverse();
  }

  return allLoans;
};

// API Response Types for Analytics
export type SecfiLoanAnalyticsInterest = z.infer<
  typeof secfiLoanAnalyticsInterestSchema
>;

export type SecfiLoanAnalytics = z.infer<typeof secfiLoanAnalyticsSchema>;

export interface GetAllSecfiLoansParams {
  accountId: string;
  start?: string;
  end?: string;
  pageLimit?: number;
  sortOrder?: "asc" | "desc";
}

export interface GetSecfiLoanAnalyticsParams {
  accountId: string;
  start?: string;
  end?: string;
}

export const getSecfiLoanAnalytics = async ({
  accountId,
  start,
  end,
}: GetSecfiLoanAnalyticsParams): Promise<SecfiLoanAnalytics> => {
  // Calculate tomorrow's date for default
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0]; // YYYY-MM-DD format

  // Use provided dates or default to 1970-01-01 to tomorrow
  const startDate = start || "1970-01-01";
  const endDate = end || tomorrowStr;

  const res = await instance.get<SecfiLoanAnalytics>(
    `/fpsl/analytics/${accountId}/loans`,
    {
      params: {
        start: startDate,
        end: endDate,
      },
    },
  );

  return looseParse(
    secfiLoanAnalyticsSchema,
    res.data,
    "getSecfiLoanAnalytics",
  );
};
